// import { createWeb3Modal } from '@web3modal/wagmi/react';
// import { defaultWagmiConfig } from '@web3modal/wagmi/react/config';
// import { WagmiProvider } from 'wagmi';
// import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
// import WalletConnectContext from './WalletConnectContext';

// const queryClient = new QueryClient();

// const PROJECT_ID = import.meta.env.VITE_PROJECT_ID;

// const metadata = {
//   name: 'Web3Modal',
//   description: 'WalletConnect',
//   url: 'https://cryllex.com',
//   icons: ['https://avatars.githubusercontent.com/u/37784886']
// }

// const WalletConnectComponent = ({ chains = [], children }) => {

//   const config = defaultWagmiConfig({
//     chains, // required
//     projectId: PROJECT_ID, // required
//     metadata, // required
//     auth: {
//       email: false,
//       socials: [],
//       showWallets: true,
//       walletFeatures: true,
//     }
//   });

//   createWeb3Modal({
//     wagmiConfig: config,
//     projectId: PROJECT_ID,
//     enableAnalytics: true,
//   });

//   return (
//     <WagmiProvider config={config}>
//       <QueryClientProvider client={queryClient}>
//         <WalletConnectContext.Provider value={config}>
//           {children}
//         </WalletConnectContext.Provider>
//       </QueryClientProvider>
//     </WagmiProvider>
//   );
// }

// export default WalletConnectComponent;



// const WalletConnectComponent = ({ chains = [], children }) => {
//   return <div>
//     {children}
//   </div>
// }

// export default WalletConnectComponent



import { useState, useEffect } from 'react'
import { WagmiProvider } from 'wagmi'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { cleanupEvmStorage } from './utils/walletCleanup'

// Lazy initialization - only when component mounts
let wagmiAdapter = null;
let queryClient = null;
let appKitInitialized = false;

export default function WalletConnectComponent({ children }) {
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Initialize only when component mounts
    const initWalletConnect = async () => {
      try {
        // Cleanup any existing EVM storage first
        cleanupEvmStorage();
        // Only initialize once
        if (appKitInitialized && wagmiAdapter && queryClient) {
          setIsReady(true);
          return;
        }

        // Dynamic imports for lazy loading
        const { createAppKit } = await import('@reown/appkit/react');
        const { WagmiAdapter } = await import('@reown/appkit-adapter-wagmi');
        const { arbitrum, avalanche, base, bsc, mainnet, optimism, polygon } = await import("@reown/appkit/networks");

        // Note: Excluding tron from EVM chains to prevent conflicts
        const chains = [arbitrum, avalanche, base, bsc, mainnet, optimism, polygon];
        const projectId = import.meta.env.VITE_PROJECT_ID;

        if (!projectId) {
          throw new Error('VITE_PROJECT_ID is not set in environment variables');
        }

        const metadata = {
          name: 'Cryllex',
          description: ' ',
          url: 'https://cryllex.com',
          icons: ['https://cryllex.com/favicon.ico']
        };

        // Create QueryClient
        queryClient = new QueryClient();

        // Create Wagmi Adapter — storage: null + reconnectOnMount={false} below prevent auto-connect on load
        wagmiAdapter = new WagmiAdapter({
          networks: chains,
          projectId,
          ssr: false,
          storage: null,
        });

        // Create AppKit modal (only once)
        if (!appKitInitialized) {
          createAppKit({
            adapters: [wagmiAdapter],
            networks: chains,
            projectId,
            metadata,
            features: {
              email: false,
              analytics: false,
              socials: [],
            },
            // Always show WalletConnect, All Wallets, and browser extension wallets
            enableWallets: true,
            // Ensure browser extension wallets are always shown
            enableEIP6963: true, // Enable browser extension wallet detection (EIP-6963)
            enableInjected: true, // Enable injected wallets (browser extensions)
            // Don't exclude any wallets - show all available including WalletConnect and All Wallets
            excludeWalletIds: [],
            // Ensure All Wallets section is always visible
            allWallets: 'SHOW',
            // No auto-reconnect on page load / init (Reown AppKit)
            // https://docs.reown.com/appkit/react/core/options#enablereconnect
            enableReconnect: false,
            // Hide "Haven't got a wallet? Get started" block in modal
            enableWalletGuide: false,
          });
          appKitInitialized = true;
        }

        setIsReady(true);
        console.log('✅ WalletConnect (EVM) initialized');
      } catch (err) {
        console.error('❌ Error initializing WalletConnect:', err);
        setError(err.message);
      }
    };

    initWalletConnect();

    return () => {
      const config = wagmiAdapter?.wagmiConfig;
      if (!config) return;
      import('@wagmi/core').then(({ disconnect, getConnections }) => {
        getConnections(config).forEach(({ connector }) => {
          disconnect(config, { connector }).catch(() => { });
        });
      });
    };
  }, []);

  if (error) {
    return (
      <div className="p-3 text-danger">
        <p>Error initializing wallet: {error}</p>
        <p className="small">Please refresh the page or contact support.</p>
      </div>
    );
  }

  if (!isReady || !wagmiAdapter || !queryClient) {
    return (
      <div className="p-3">
        <div className="spinner-border spinner-border-sm" role="status">
          <span className="visually-hidden">Loading wallet...</span>
        </div>
        <span className="ms-2">Initializing wallet connection...</span>
      </div>
    );
  }

  return (
    <WagmiProvider config={wagmiAdapter.wagmiConfig} reconnectOnMount={false}>
      <QueryClientProvider client={queryClient}>
        {children}
      </QueryClientProvider>
    </WagmiProvider>
  );
}